import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  Company,
  formulationBadgeClass,
  FormulationType,
  formulationLabel,
  Product,
  ProductCategoryObject,
} from '../../product.model';
import { ProductService } from '../../product.service';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { Action, PaginatedResponse } from '../../../common/models/common.model';
import { buildFilterQuery, getDefaultProfilePicUrl } from '../../../utils';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { MeasurementUnit } from '../../../raw-material-inventory/raw-material.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { ErrorParserService, ParsedError } from '../../../services/error-parser.service';

@Component({
  selector: 'app-products',
  standalone: false,
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
})
export class ProductsComponent implements OnInit, OnDestroy {
  @ViewChild('productModal') productModalRef!: TemplateRef<any>;
  @ViewChild('manageCategoriesModal') manageCategoriesModalRef!: TemplateRef<any>;
  @ViewChild('manageFormulationTypesModal') manageFormulationTypesModalRef!: TemplateRef<any>;

  protected products: Product[] = [];
  protected isDataLoading: boolean = true;
  protected searchTerm: string = '';
  protected selectedCategory: string | null = null;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected totalCount: number = 0;

  protected companies: Company[] = [];
  protected selectedCompany: string | null = null;

  // Product modal variables
  protected productForm!: FormGroup;
  protected isEditMode = false;
  protected modalRef!: NgbModalRef;
  protected imagePreview: string | ArrayBuffer | null = null;
  protected selectedImageFile: File | null = null;
  protected isProductInfoSaving: boolean = false;
  protected productUnitOptions: MeasurementUnit[] = [];
  private searchDebounceTimer!: any;

  // Formulation types
  protected formulationTypes: FormulationType[] = [];
  protected isFormulationTypesLoading: boolean = false;
  protected isAddingNewFormulationType = false;
  protected newFormulationLabel = '';
  protected isCreatingFormulationType = false;
  protected formulationCreateError: string | null = null;

  /** Field errors from the last rejected save, e.g. `{"formulation_type": ["..."]}` from DRF. */
  protected serverFieldErrors: ParsedError[] = [];

  // Category
  protected productCategories: ProductCategoryObject[] = [];
  protected isAddingNewCategory = false;
  protected newCategoryName = '';
  protected isCreatingCategory = false;
  protected deletingCategoryId: string | null = null;

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;

  private editingProductId: string | null = null;
  protected isExportingProducts: boolean = false;
  private _productSubscription!: Subscription;
  protected currentPage: number = 1;

  constructor(
    private _productService: ProductService,
    private _modalService: NgbModal,
    private _rawMaterialService: RawMaterialService,
    private _companyFactoryService: CompanyFactoryService,
    private _errorParserService: ErrorParserService
  ) {}

  ngOnInit(): void {
    this.getUnits();
    this._getProductList();
    this._loadCategories();
    this._loadCompanies();
    this._loadFormulationTypes();
  }

  private _loadFormulationTypes(): void {
    this.isFormulationTypesLoading = true;
    this._productService
      .listFormulationTypes()
      .pipe(finalize(() => (this.isFormulationTypesLoading = false)))
      .subscribe({
        next: (types: FormulationType[]) => {
          this.formulationTypes = types;
        },
      });
  }

  private _loadCategories(): void {
    this._productService.listCategories().subscribe({
      next: categories => {
        this.productCategories = categories;
      },
    });
  }

  private _loadCompanies(): void {
    this._companyFactoryService.listCompanies().subscribe({
      next: (companies: Company[]) => {
        this.companies = companies;
      },
    });
  }

  private getUnits(): void {
    const units$ = this._rawMaterialService.get_units();

    units$.subscribe({
      next: (units: MeasurementUnit[]) => {
        this.productUnitOptions = units;
        this._initializeProductFormGroup();
      },
    });
  }

  // Protected methods

  protected onAddNewProduct(): void {
    this.isEditMode = false;
    this.editingProductId = null;
    this.imagePreview = null;
    this.selectedImageFile = null;
    this.isAddingNewCategory = false;
    this.newCategoryName = '';
    this.isAddingNewFormulationType = false;
    this.newFormulationLabel = '';
    this.formulationCreateError = null;
    this.serverFieldErrors = [];
    this._initializeProductFormGroup();

    const modalOptions: NgbModalOptions = { backdrop: 'static', size: 'lg' };
    this._openModal(this.productModalRef, modalOptions);
  }

  protected onEditProduct(product: Product) {
    this.isEditMode = true;
    this.editingProductId = product.id;
    this.isAddingNewCategory = false;
    this.newCategoryName = '';
    this.isAddingNewFormulationType = false;
    this.newFormulationLabel = '';
    this.formulationCreateError = null;
    this.serverFieldErrors = [];

    const formulationTypeId =
      typeof product.formulation_type === 'object'
        ? (product.formulation_type?.id ?? null)
        : (product.formulation_type ?? null);

    this.productForm.patchValue({
      name: product.name,
      contents: product.contents,
      product_category: product.product_category?.id ?? null,
      hsn_code: product.hsn_code,
      // A product from before the field shipped arrives as null. Left null on purpose:
      // the required validator then blocks save until someone actually picks one, rather
      // than defaulting to Liquid and writing a formulation nobody checked.
      formulation_type: formulationTypeId,
      company: product.company?.id ?? null,
    });

    // Without this the user meets a disabled Update button and no reason for it, since
    // `isInvalid` waits for touched/dirty. Marking it touched states the ask up front.
    if (!formulationTypeId) {
      this.productForm.get('formulation_type')?.markAsTouched();
    }

    this.imagePreview = null;
    this.selectedImageFile = null;

    const modalOptions: NgbModalOptions = { backdrop: 'static', size: 'lg' };
    this._openModal(this.productModalRef, modalOptions);
  }

  protected onDeleteProduct(productId: string): void {
    if (!productId) {
      return;
    }

    const modalRef = this._modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = 'Delete Product';
    modalRef.componentInstance.message = 'Are you sure you want to delete this product?';
    modalRef.componentInstance.warning = 'This action cannot be undone and the product will be permanently removed.';

    modalRef.result.then(() => {
      this._productSubscription = this._productService.delete(productId).subscribe({
        next: () => {
          this.products = this.products.filter((product: Product) => {
            return product.id !== productId;
          });

          this.totalCount = this.totalCount ? this.totalCount - 1 : 0;
          if (this.totalCount ?? 0 <= this.products.length) {
            this.hasNextPage = false;
            this.hasPreviousPage = false;
          }
        },
      });
    });
  }

  protected onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this._getProductList();
    }, 500);
  }

  protected onCategoryChange(): void {
    this.currentPage = 1;
    this._getProductList();
  }

  protected onToggleAddCategory(): void {
    this.isAddingNewCategory = !this.isAddingNewCategory;
    this.newCategoryName = '';
  }

  protected onCreateCategory(autoSelect = true): void {
    const name = this.newCategoryName.trim();
    if (!name) return;

    this.isCreatingCategory = true;
    this._productService
      .createCategory(name)
      .pipe(finalize(() => (this.isCreatingCategory = false)))
      .subscribe({
        next: category => {
          this.productCategories = [...this.productCategories, category];
          if (autoSelect) {
            this.productForm.get('product_category')?.setValue(category.id);
            this.productForm.get('product_category')?.markAsDirty();
          }
          this.isAddingNewCategory = false;
          this.newCategoryName = '';
        },
      });
  }

  protected onManageCategories(): void {
    this.isAddingNewCategory = false;
    this.newCategoryName = '';
    this._openModal(this.manageCategoriesModalRef, { backdrop: 'static', size: 'md' });
  }

  protected onToggleAddFormulationType(): void {
    this.isAddingNewFormulationType = !this.isAddingNewFormulationType;
    this.newFormulationLabel = '';
    this.formulationCreateError = null;
  }

  protected onCreateFormulationType(autoSelect = true): void {
    const label = this.newFormulationLabel.trim();
    if (!label) return;

    // For now key is passed the same as label from FE
    const key = label;

    this.isCreatingFormulationType = true;
    this.formulationCreateError = null;

    this._productService
      .createFormulationType({ key, label })
      .pipe(finalize(() => (this.isCreatingFormulationType = false)))
      .subscribe({
        next: (created: FormulationType) => {
          this.formulationTypes = [...this.formulationTypes, created];
          if (autoSelect) {
            this.productForm.get('formulation_type')?.setValue(created.id);
            this.productForm.get('formulation_type')?.markAsDirty();
          }
          this.isAddingNewFormulationType = false;
          this.newFormulationLabel = '';
        },
        error: (err: unknown) => {
          const errors = this._errorParserService.parseError(err);
          this.formulationCreateError = errors.length > 0 ? errors[0].message : 'Failed to create formulation type';
        },
      });
  }

  protected onManageFormulationTypes(): void {
    this.isAddingNewFormulationType = false;
    this.newFormulationLabel = '';
    this.formulationCreateError = null;
    this._openModal(this.manageFormulationTypesModalRef, { backdrop: 'static', size: 'md' });
  }

  protected onDeleteCategory(categoryId: string): void {
    this.deletingCategoryId = categoryId;
    this._productService
      .deleteCategory(categoryId)
      .pipe(finalize(() => (this.deletingCategoryId = null)))
      .subscribe({
        next: () => {
          this.productCategories = this.productCategories.filter(c => c.id !== categoryId);
          // Clear filter if the deleted category was selected
          if (this.selectedCategory === categoryId) {
            this.selectedCategory = null;
            this._getProductList();
          }
        },
      });
  }

  protected onSubmitProduct(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    this.serverFieldErrors = [];
    this.isProductInfoSaving = true;

    if (this.isEditMode && this.editingProductId) {
      if (this.selectedImageFile) {
        const formData = new FormData();

        Object.keys(this.productForm.controls).forEach(key => {
          const control = this.productForm.get(key);
          if (control && control.dirty) {
            formData.append(key, control.value);
          }
        });

        formData.append('image_url', this.selectedImageFile);
        this._makePatchRequest(formData);
      } else {
        const payload: any = {};

        Object.keys(this.productForm.controls).forEach(key => {
          const control = this.productForm.get(key);
          if (control && control.dirty) {
            payload[key] = control.value;
          }
        });

        this._makePatchRequest(payload);
      }
    } else {
      const formData = new FormData();

      Object.keys(this.productForm.controls).forEach(key => {
        const control = this.productForm.get(key);
        formData.append(key, control?.value);
      });

      if (this.selectedImageFile) {
        formData.append('image_url', this.selectedImageFile);
      } else {
        formData.append('image_url', '');
      }

      this._makePostRequest(formData);
    }
  }

  private _makePatchRequest(payload: FormData | any): void {
    this._productSubscription = this._productService
      .patch(this.editingProductId!, payload)
      .pipe(
        finalize(() => {
          this.isProductInfoSaving = false;
        })
      )
      .subscribe({
        next: (product: Product) => {
          const index = this.products.findIndex(p => p.id === this.editingProductId);
          if (index !== -1) this.products[index] = product;

          this._getProductList();
          this.modalRef?.dismiss();
        },
        error: err => this._captureFieldErrors(err),
      });
  }

  private _makePostRequest(formData: FormData): void {
    this._productSubscription = this._productService
      .post(formData)
      .pipe(
        finalize(() => {
          this.isProductInfoSaving = false;
        })
      )
      .subscribe({
        next: (product: Product) => {
          this.products.push(product);
          this._getProductList();
          this.modalRef?.dismiss();
        },
        error: err => this._captureFieldErrors(err),
      });
  }

  protected onRemoveImage(): void {
    this.imagePreview = null;
    this.selectedImageFile = null;

    const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
    if (fileInput) fileInput.value = '';
  }

  protected isInvalid(controlName: string): boolean {
    const control = this.productForm.get(controlName);
    return (control?.invalid && (control?.touched || control?.dirty)) ?? false;
  }

  /** The rejected save's message for one field, shown under that control. */
  protected getServerError(controlName: string): string | null {
    return this._errorParserService.getFieldError(this.serverFieldErrors, controlName);
  }

  protected readonly formulationLabel = formulationLabel;
  protected readonly formulationBadgeClass = formulationBadgeClass;

  private _captureFieldErrors(err: unknown): void {
    this.serverFieldErrors = this._errorParserService.parseError(err);
  }

  protected onImageSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedImageFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  protected onChangeCompany(): void {
    this.currentPage = 1;
    this._getProductList();
  }

  protected _getProductList(): void {
    this.isDataLoading = true;

    this.products = [];

    const reqParams = buildFilterQuery({
      page: this.currentPage,
      product_category: this.selectedCategory,
      search: this.searchTerm,
      company: this.selectedCompany,
    });

    this._productSubscription?.unsubscribe();
    this._productSubscription = this._productService
      .list(reqParams)
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (productListResponse: PaginatedResponse<Product>) => {
          this.products = productListResponse.results;
          this.hasNextPage = !!productListResponse.next;
          this.hasPreviousPage = !!productListResponse.previous;
          this.totalCount = productListResponse.count;
        },
      });
  }

  private _initializeProductFormGroup(): void {
    this.productForm = new FormGroup({
      name: new FormControl('', Validators.required),
      contents: new FormControl(''),
      product_category: new FormControl(null, Validators.required),
      hsn_code: new FormControl(''),
      // Required in the frontend only: the backend field is optional and a POST without it
      // returns 201 with null, so the form is what keeps an unrecorded formulation out.
      formulation_type: new FormControl<string | null>(null, Validators.required),
      company: new FormControl(null),
    });
  }

  private _openModal(content: TemplateRef<any>, options: NgbModalOptions): void {
    this.modalRef = this._modalService.open(content, options);
  }

  protected onExportProducts(): void {
    this.isExportingProducts = true;
    this._productService
      .export()
      .pipe(finalize(() => (this.isExportingProducts = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'products.xlsx';
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  ngOnDestroy(): void {
    this._productSubscription?.unsubscribe();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getProductList();
  }
}
